import { Component, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, switchMap } from 'rxjs/operators';
import { RegistrierungService } from '../../services/registrierung.service';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Laender } from '../../model/konstanten';

import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { FirmendatenMTO } from '../../generated-model/firmendatenMTO';
import { RegistrierungComponent } from '../registrierung.component';
import { AdressSucheComponent } from '../adress-suche/adress-suche.component';
import { MatIcon } from '@angular/material/icon';
import { OsmAdresse } from '../../generated-model/osmAdresse';
import { TranslatePipe } from '@ngx-translate/core';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-firmendaten',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    AdressSucheComponent,
    MatError,
    MatIcon,
    TranslatePipe,
    MatCheckbox,
  ],
  templateUrl: './firmendaten.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './firmendaten.component.scss',
})
export class FirmendatenComponent {
  private readonly registrierungService = inject(RegistrierungService);
  protected registrierungComponent = inject(RegistrierungComponent);

  // Signal inputs and state
  formGroupFirma = input<FormGroup>({} as FormGroup);
  adresseGeprueft = signal<boolean>(false);
  showAdresse = signal<boolean>(false);
  firmendaten = signal<FirmendatenMTO>({} as FirmendatenMTO);

  constructor() {
    // Setup auto-save effect on form value changes
    effect(() => {
      const fg = this.formGroupFirma();
      if (fg?.valueChanges) {
        fg.valueChanges
          .pipe(
            debounceTime(200),
            switchMap((value) => {
              this.updateFirmendaten(value);
              return this.registrierungService.saveRegistrierungsdatenToLocalStorage();
            })
          )
          .subscribe((result) => {
            if (result === false) {
              console.log('formular failure: autosave not executed');
            }
          });
      }
    });

    // Trigger parent form validity update when adressPruefung changes
    effect(() => {
      //Bei effecten muss man immer darauf achten, dass nur die geprüft werden, die in der effect Funktion auch wirklich
      // aufgeführt werden, da sonst unnötige Effekte ausgelöst werden können.
      // In diesem Fall ist es die adresseGeprueft(), die in der effect Funktion benutzt wird,
      // um die updateFormValidityCache Funktion des Elternteils aufzurufen.
      // daher ist der IDE Hinweis auf nicht genutzte Variable irritierend
      const adresseGeprueft = this.adresseGeprueft();
      this.registrierungComponent.updateFormValidityCache();
    });
  }

  private updateFirmendaten(value: any): void {
    // Zunächst prüfen, ob es sich um Testfirme @test@ handelt
    this.registrierungComponent.updateProduktionSimulation(value.simuliereProd);
    this.registrierungComponent.updateTestFirma(value.firma);
    const currentFirmendaten = this.firmendaten();
    const testUnternehmenAnlage = this.registrierungService.registrierungsdaten.firmendaten?.test;
    const updatedFirmendaten: FirmendatenMTO = {
      ...currentFirmendaten,
      firma: value.firma ?? currentFirmendaten.firma,
      name: value.name ?? currentFirmendaten.name,
      strasse: value.strasse ?? currentFirmendaten.strasse,
      hausnummer: value.hausnummer ?? currentFirmendaten.hausnummer,
      plz: value.plz ?? currentFirmendaten.plz,
      ort: value.ort ?? currentFirmendaten.ort,
      land: value.land ?? currentFirmendaten.land,
      telefon: value.telefon ?? currentFirmendaten.telefon,
      mobil: value.mobil ?? currentFirmendaten.mobil,
      steuernummer: value.steuernummer ?? currentFirmendaten.steuernummer,
      ustId: value.ustId ?? currentFirmendaten.ustId,
      handelsregister: value.handelsregister ?? currentFirmendaten.handelsregister,
      homepage: value.homepage ?? currentFirmendaten.homepage,
      test: testUnternehmenAnlage ?? currentFirmendaten.test,
    };
    if (
      updatedFirmendaten.steuernummer &&
      updatedFirmendaten.steuernummer !== currentFirmendaten.steuernummer &&
      updatedFirmendaten.steuernummer.length == 11
    ) {
      this.checkSteuernummerEindeutig(updatedFirmendaten.steuernummer);
    }
    this.firmendaten.set(updatedFirmendaten);
    this.registrierungService.registrierungsdaten.firmendaten = updatedFirmendaten;
  }

  get LaenderEnums() {
    return Laender;
  }

  onAdressChanged(adresse: any) {
    this.showAdresse.set(false);
    this.adresseGeprueft.set(false);
    const fg = this.formGroupFirma();
    if (fg) {
      fg.patchValue({
        strasse: adresse.strasse,
        plz: adresse.plz,
        ort: adresse.ort,
        hausnummer: adresse.hausnummer,
        land: adresse.laenderkennzeichen,
      });
    }
    if (this.registrierungService.registrierungsdaten.firmendaten && adresse.lat && adresse.lon) {
      this.registrierungService.registrierungsdaten.firmendaten.lat = adresse.lat;
      this.registrierungService.registrierungsdaten.firmendaten.lon = adresse.lon;
      this.registrierungService.registrierungsdaten.firmendaten.land = adresse.land;
    }
    // Defer signal updates to next event loop to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.adresseGeprueft.set(true);
      this.registrierungComponent.adressPruefung.set(true);
    });
  }

  getOsmAdresse(): OsmAdresse {
    return this.firmendaten() || ({} as FirmendatenMTO);
  }

  private checkSteuernummerEindeutig(steuernummer: string) {
    this.registrierungService.checkSteuernummerGueltig(steuernummer).subscribe((response) => {
      if (!response.success) {
        this.formGroupFirma().get('steuernummer')?.setErrors({ error: true, invalid: true });
        this.formGroupFirma().get('steuernummer')?.markAsTouched();
      }
    });
  }

  isTestUmgebung(): boolean {
    return this.registrierungComponent.testUmgebung();
  }
}
